import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { 
  XMarkIcon, 
  MagnifyingGlassIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { contactService } from '../../services/ContactService';
import { auth } from '../../firebase';
import Avatar from '../Avatar';

function ContactPickerModal({ isOpen, onClose, onSelectContacts, initialSelectedContactIds }) {
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  // Filter out empty, null, or undefined IDs
  const [selectedIds, setSelectedSelectedIds] = useState(new Set((initialSelectedContactIds || []).filter(id => id && id.trim())));
  const currentUser = auth.currentUser;

  useEffect(() => {
    if (isOpen) {
        // Reset selection to initial props when opening, filtering out invalid IDs
        setSelectedSelectedIds(new Set((initialSelectedContactIds || []).filter(id => id && id.trim())));
    }
  }, [isOpen, initialSelectedContactIds]);

  useEffect(() => {
    if (!currentUser) return;
    const unsubscribe = contactService.subscribeToContacts(currentUser.uid, (data) => {
      setContacts(data);
    });
    return () => unsubscribe();
  }, [currentUser]);

  const filteredContacts = useMemo(() => {
    if (!searchTerm) return contacts;
    const term = searchTerm.toLowerCase();
    return contacts.filter(c => 
        c.name.toLowerCase().includes(term) || 
        (c.surname && c.surname.toLowerCase().includes(term)) ||
        (c.relatedAccountName && c.relatedAccountName.toLowerCase().includes(term))
    );
  }, [contacts, searchTerm]);

  const toggleSelection = (contactId) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(contactId)) {
      newSet.delete(contactId);
    } else {
      newSet.add(contactId);
    }
    setSelectedSelectedIds(newSet);
  };

  const handleSave = () => {
    // Map IDs back to full contact objects (light version for note)
    const selectedContacts = contacts
        .filter(c => selectedIds.has(c.id))
        .map(c => ({
            id: c.id,
            contactName: c.name + (c.surname ? ` ${c.surname}` : ''),
            lastNameUpdateDate: Date.now()
        }));
    
    onSelectContacts(selectedContacts);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70] flex items-center justify-center p-4">
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Add Contacts</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-100">
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                    type="text"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    placeholder="Search contacts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-2">
            {filteredContacts.map(contact => {
                const isSelected = selectedIds.has(contact.id);
                const isPerson = contact.type === 'person';
                return (
                    <div 
                        key={contact.id}
                        onClick={() => toggleSelection(contact.id)}
                        className={`flex items-center p-3 rounded-md cursor-pointer transition-colors ${
                            isSelected ? 'bg-orange-50 border border-orange-200' : 'hover:bg-gray-50 border border-transparent'
                        }`}
                    >
                        <div className="flex-shrink-0 mr-3">
                            <Avatar 
                                name={contact.name} 
                                surname={contact.surname} 
                                imageURL={contact.imageURL} 
                                type={contact.type}
                                size="sm"
                            />
                        </div>
                        <div className="flex-1">
                            <p className={`text-sm font-medium ${isSelected ? 'text-orange-900' : 'text-gray-900'}`}>
                                {contact.name} {isPerson && contact.surname}
                            </p>
                            {isPerson && contact.relatedAccountName && (
                                <p className="text-xs text-gray-500">{contact.relatedAccountName}</p>
                            )}
                        </div>
                        {isSelected && (
                            <CheckIcon className="h-5 w-5 text-orange-600" />
                        )}
                    </div>
                );
            })}
            {filteredContacts.length === 0 && (
                <p className="text-center text-gray-500 py-4">No contacts found</p>
            )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end shrink-0">
            <button
                onClick={handleSave}
                className="px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
                Done
            </button>
        </div>
      </div>
    </div>
  );
}

ContactPickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectContacts: PropTypes.func.isRequired,
  initialSelectedContactIds: PropTypes.arrayOf(PropTypes.string)
};

export default ContactPickerModal;

